import { arrayToObject } from 'lib/array/arrayUtils'
import { apiReducerSwitch } from 'lib/redux/reducerUtils'
import { BUSINESS_TRAVELLER_INITIAL_STATE } from './businessTravellerActionReducer'
import { USER_LOGOUT } from 'actions/apiActionConstants'

export enum BUSINESS_TRAVELLER_API_ACTIONS {
  FETCH_ACCOUNT_BUSINESS = 'BUSINESS_TRAVELLER_FETCH_ACCOUNT_BUSINESS',
  FETCH_APPROVAL_REQUEST = 'BUSINESS_TRAVELLER_FETCH_APPROVAL_REQUEST',
  FETCH_APPROVAL_REQUEST_APPROVALS = 'BUSINESS_TRAVELLER_FETCH_APPROVAL_REQUEST_APPROVALS',
  FETCH_OFFER_CREDITS = 'BUSINESS_TRAVELLER_FETCH_OFFER_CREDITS',
  FETCH_MULTIPLE_OFFERS_CREDITS = 'BUSINESS_TRAVELLER_FETCH_MULTIPLE_OFFERS_CREDITS',
  FETCH_BUSINESS_BUDGET = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_BUDGET',
  FETCH_BUSINESS_ACCOMMODATION_POLICY = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_ACCOMMODATION_POLICY',
  FETCH_BUSINESS_FLIGHT_POLICY = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_FLIGHT_POLICY',
  FETCH_BUSINESS_CAR_HIRE_POLICY = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_CAR_HIRE_POLICY',
  FETCH_BUSINESS_FLIGHT_BUDGET = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_FLIGHT_BUDGET',
  FETCH_BUSINESS_CAR_HIRE_BUDGET = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_CAR_HIRE_BUDGET',
  UPDATE_EMPLOYEE_ONBOARDING_STATUS = 'BUSINESS_TRAVELLER_UPDATE_EMPLOYEE_ONBOARDING_STATUS',
  UPDATE_EMPLOYEE_ONBOARDING_FLAGS = 'BUSINESS_TRAVELLER_UPDATE_EMPLOYEE_ONBOARDING_FLAGS',
  UPDATE_EMPLOYEE_DISPLAY_FLIGHT_BUDGET_STATUS = 'BUSINESS_TRAVELLER_UPDATE_EMPLOYEE_DISPLAY_FLIGHT_BUDGET_STATUS',
  UPDATE_EMPLOYEE_LOYALTY_PROGRAM = 'BUSINESS_TRAVELLER_UPDATE_EMPLOYEE_LOYALTY_PROGRAM',
  FETCH_BUSINESS_EMPLOYEES = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_EMPLOYEES',
  FETCH_BUSINESS_APPROVERS = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_APPROVERS',
  FETCH_BUSINESS_FLIGHT_BUDGET_SEARCH = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_FLIGHT_SEARCH',
  FETCH_BUSINESS_APPROVAL_POLICY = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_APPROVAL_POLICY',
  FETCH_BUSINESS_EMPLOYEE_DETAILS = 'BUSINESS_TRAVELLER_FETCH_EMPLOYEE_DETAILS',
  FETCH_BUSINESS_SERVICE_FEES = 'BUSINESS_TRAVELLER_FETCH_BUSINESS_SERVICE_FEES',
  USER_LINK_PERSONAL_ACCOUNT = 'BUSINESS_TRAVELLER_USER_LINK_PERSONAL_ACCOUNT',
}

export const apiCalls = apiReducerSwitch<App.BusinessTravellerState>({
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_ACCOUNT_BUSINESS]: {
    request: () => {
      return {
        currentBusinessAccount: { status: 'pending' },
        businessOffices: { status: 'pending' },
        businessSettings: { status: 'pending' },
        onboarding: { status: 'pending' },
        employees: { status: 'pending' },
        businessCards: { status: 'pending', cards: [] },
      }
    },
    success: (state, action) => {
      const businessAccount = action.data.businessAccount
      const businessCards = action.data.businessCards

      return {
        currentBusinessAccount: {
          status: 'success',
          business: businessAccount.business,
          employee: businessAccount.employee,
        },
        businessOffices: {
          status: 'success',
          all: arrayToObject(businessAccount.businessOffices, (office: App.BusinessTraveller.BusinessOffice) => office.id),
        },
        businessSettings: {
          status: 'success',
          businessSettings: businessAccount.businessSettings,
        },
        onboarding: {
          status: 'success',
          employeeOnboardingStatus: businessAccount.employeeOnboardingStatus,
        },
        flightBudgetNotification: {
          status: 'success',
          displayFlightBudgetNotification: businessAccount.displayFlightBudgetNotification,
        },
        employees: {
          status: 'success',
          all: arrayToObject(businessAccount.employees, (employee: App.BusinessTraveller.EmployeeFromMeEndpoint) => employee.id),
        },
        businessCards: {
          status: 'success',
          cards: businessCards,
        },
      }
    },
    failure: (state, action) => {
      return {
        currentBusinessAccount: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
        businessOffices: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
        businessSettings: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
        onboarding: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
        employees: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
        businessCards: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_APPROVAL_REQUEST]: {
    request: () => {
      return {
        approvalRequests: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        approvalRequests: {
          status: 'success',
          request: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        approvalRequests: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_APPROVAL_REQUEST_APPROVALS]: {
    request: () => {
      return {
        approvalRequestsApprovals: { status: 'pending', request: [] },
      }
    },
    success: (state, action) => {
      return {
        approvalRequestsApprovals: {
          status: 'success',
          request: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        approvalRequestsApprovals: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.UPDATE_EMPLOYEE_ONBOARDING_STATUS]: {
    request: () => {
      return {
        onboarding: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        onboarding: {
          status: 'success',
          employeeOnboardingStatus: action.employeeOnboardingStatus,
        },
      }
    },
    failure: (state, action) => {
      return {
        onboarding: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.UPDATE_EMPLOYEE_DISPLAY_FLIGHT_BUDGET_STATUS]: {
    request: () => {
      return {
        flightBudgetNotification: { status: 'pending' },
      }
    },
    success: () => {
      return {
        flightBudgetNotification: {
          status: 'success',
          displayFlightBudgetNotification: false,
        },
      }
    },
    failure: (state, action) => {
      return {
        flightBudgetNotification: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_OFFER_CREDITS]: {
    request: (state, action) => {
      return {
        offersCredits: {
          ...state.offersCredits,
          [action.creditItemKey]: { status: 'pending' },
        },
      }
    },
    success: (state, action) => {
      return {
        offersCredits: {
          ...state.offersCredits,
          [action.creditItemKey]: {
            status: 'success',
            ...action.data,
          },
        },
      }
    },
    failure: (state, action) => {
      return {
        offersCredits: {
          ...state.offersCredits,
          [action.creditItemKey]: { status: 'failed' },
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_MULTIPLE_OFFERS_CREDITS]: {
    request: (state, action) => {
      const requestedOfferCredits = Object.fromEntries<App.BusinessTraveller.StatefulData<App.BusinessTraveller.OfferCredit>>(
        Object.keys(action.toBeCalculatedOfferCredits)
          .map((offerCreditKey) => ([offerCreditKey, { status: 'pending' }])),
      )
      return {
        offersCredits: {
          ...state.offersCredits,
          ...requestedOfferCredits,
        },
      }
    },
    success: (state, action) => {
      const fetchedOfferCredits = Object.fromEntries<App.BusinessTraveller.StatefulData<App.BusinessTraveller.OfferCredit>>(
        Object.entries<App.BusinessTraveller.OfferCredit>(action.data)
          .map(([offerCreditKey, calculatedCredit]) => ([
            offerCreditKey,
            calculatedCredit.budgetCompliance === 'under_budget' ? {
              status: 'success',
              budgetCompliance: 'under_budget',
              creditValue: calculatedCredit.creditValue,
              budgetComparison: calculatedCredit.budgetComparison,
            } : {
              status: 'success',
              budgetCompliance: calculatedCredit.budgetCompliance,
              creditValue: 0,
              budgetComparison: calculatedCredit.budgetComparison,
            },
          ])),
      )
      return {
        offersCredits: {
          ...state.offersCredits,
          ...fetchedOfferCredits,
        },
      }
    },
    failure: (state, action) => {
      const failedOfferCredits = Object.fromEntries<App.BusinessTraveller.StatefulData<App.BusinessTraveller.OfferCredit>>(
        Object.keys(action.toBeCalculatedOfferCredits)
          .map((offerCreditKey) => ([offerCreditKey, { status: 'failed' }])),
      )
      return {
        offersCredits: {
          ...state.offersCredits,
          ...failedOfferCredits,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_BUDGET]: {
    request: (state, action) => {
      return {
        businessBudgets: {
          ...state.businessBudgets,
          [action.budgetKey]: { status: 'pending' },
        },
        businessMapBudget: undefined,
      }
    },
    success: (state, action) => {
      const currentBudget = action.data.budgets.length > 0 ? action.data.budgets[0] : undefined

      return {
        businessBudgets: {
          ...state.businessBudgets,
          [action.budgetKey]: {
            status: 'success',
            maxNightlyBudget: action.data.maxNightlyBudget,
            minNightlyBudget: action.data.minNightlyBudget,
            budgets: action.data.budgets,
          },
        },
        businessMapBudget: currentBudget,
      }
    },
    failure: (state, action) => {
      return {
        businessBudgets: {
          ...state.businessBudgets,
          [action.budgetKey]: { status: 'failed' },
        },
        businessMapBudget: undefined,
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_ACCOMMODATION_POLICY]: {
    request: () => {
      return {
        businessAccommodationPolicy: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        businessAccommodationPolicy: {
          status: 'success',
          policy: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        businessFlightPolicy: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_FLIGHT_POLICY]: {
    request: () => {
      return {
        businessFlightPolicy: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        businessFlightPolicy: {
          status: 'success',
          policy: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        businessFlightPolicy: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_CAR_HIRE_POLICY]: {
    request: () => {
      return {
        businessCarHirePolicy: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        businessCarHirePolicy: {
          status: 'success',
          policy: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        businessCarHirePolicy: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_FLIGHT_BUDGET]: {
    request: (state, action) => {
      return {
        businessFlightBudgets: {
          ...state.businessFlightBudgets,
          [action.budgetKey]: { status: 'pending' },
        },
      }
    },
    success: (state, action) => {
      return {
        businessFlightBudgets: {
          ...state.businessFlightBudgets,
          [action.budgetKey]: {
            status: 'success',
            flightBudgets: action.data,
          },
        },
      }
    },
    failure: (state, action) => {
      return {
        businessFlightBudgets: {
          ...state.businessFlightBudgets,
          [action.budgetKey]: { status: 'failed' },
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_CAR_HIRE_BUDGET]: {
    request: (state, action) => {
      return {
        businessCarHireBudgets: {
          ...state.businessCarHireBudgets,
          [action.budgetKey]: { status: 'pending' },
        },
      }
    },
    success: (state, action) => {
      return {
        businessCarHireBudgets: {
          ...state.businessCarHireBudgets,
          [action.budgetKey]: {
            status: 'success',
            carHireBudgets: action.data,
          },
        },
      }
    },
    failure: (state, action) => {
      return {
        businessCarHireBudgets: {
          ...state.businessCarHireBudgets,
          [action.budgetKey]: { status: 'failed' },
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_EMPLOYEES]: {
    request: () => {
      return {
        employees: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        employees: {
          status: 'success',
          all: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        employees: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_APPROVERS]: {
    request: () => {
      return {
        approvers: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        approvers: {
          status: 'success',
          all: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        approvers: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_FLIGHT_BUDGET_SEARCH]: {
    request: (state, action) => {
      return {
        flightSearchBudget: {
          ...state.flightSearchBudget,
          [action.flightSearchKey]: { status: 'pending' },
        },
      }
    },
    success: (state, action) => {
      const budgets = action.data
      const budgetIds = budgets.map(budget => budget.id)
      const newBudgets = arrayToObject(budgets, budget => budget.id)
      return {
        budgets: { ...state.budgets, ...newBudgets },
        flightSearchBudget: {
          ...state.flightSearchBudget,
          [action.flightSearchKey]: { status: 'success', budgetIds },
        },
      }
    },
    failure: (state, action) => {
      return {
        flightSearchBudget: {
          ...state.flightSearchBudget,
          [action.flightSearchKey]: { status: 'failed' },
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_APPROVAL_POLICY]: {
    request: () => {
      return {
        businessApprovalPolicy: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        businessApprovalPolicy: {
          status: 'success',
          policy: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        businessApprovalPolicy: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_EMPLOYEE_DETAILS]: {
    request: () => {
      return {
        employeeDetails: { status: 'pending' },
      }
    },
    success: (state, action) => {
      return {
        employeeDetails: {
          status: 'success',
          all: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        employeeDetails: {
          status: 'failed',
          error: action.error.message ?? action.error,
        },
      }
    },
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.FETCH_BUSINESS_SERVICE_FEES]: {
    request: (state, action) => {
      return {
        serviceFees: {
          ...state.serviceFees,
          [action.serviceFeeKey]: { status: 'pending' },
        },
      }
    },
    success: (state, action) => {
      return {
        serviceFees: {
          ...state.serviceFees,
          [action.serviceFeeKey]: {
            status: 'success',
            ...action.data,
          },
        },
      }
    },
    failure: (state, action) => {
      return {
        serviceFees: {
          ...state.serviceFees,
          [action.serviceFeeKey]: { status: 'failed' },
        },
      }
    },
  },
  [USER_LOGOUT]: {
    request: () => BUSINESS_TRAVELLER_INITIAL_STATE,
  },
  [BUSINESS_TRAVELLER_API_ACTIONS.UPDATE_EMPLOYEE_ONBOARDING_FLAGS]: {
    success: (state, action) => {
      const newFlags = action.data

      if (state.currentBusinessAccount && state.currentBusinessAccount.status === 'success' && state.currentBusinessAccount.employee) {
        const currentEmployee = state.currentBusinessAccount.employee
        const existingFlags = currentEmployee.onboardingFlags || []

        const updatedFlags = new Set([...existingFlags, ...newFlags])

        return {
          ...state,
          currentBusinessAccount: {
            ...state.currentBusinessAccount,
            employee: {
              ...currentEmployee,
              onboardingFlags: Array.from(updatedFlags),
            },
          },
        }
      }

      return state
    },
  },
})
